import type { Callback, Stop } from "shelving";
import { Suspense, createContext, useContext } from "react";
import { ArrayStore, StateError, getRandomKey } from "shelving";
import { useInstance, useMount, useStore } from "shelving/react";
import { LOADING_ELEMENT } from "../loader/Loader.js";
import { Icon } from "./Icon.js";

/** How long before a hidden dialogs are removed from the DOM (allow time for animates to complete). */
const REMOVE_DELAY = 5000;

/** Context for dialogs. */
const DIALOG_CONTEXT = createContext<DialogsStore | null>(null);

/** Store a list of dialogs. */
class DialogsStore extends ArrayStore<{ key: string; children: React.ReactNode }> {
	/** Show a new dialog in this list. */
	readonly showDialog = (children: React.ReactNode) => {
		const dialog = {
			key: getRandomKey(),
			children,
		};
		this.add(dialog);
	};
}

/** Create a new dialog context that allows child nodes to call a `showDialog()` function. */
export function Dialogs({ children }: { children: React.ReactNode }): React.ReactElement {
	return (
		<DIALOG_CONTEXT.Provider value={useInstance(DialogsStore)}>
			{children}
			<DialogList />
		</DIALOG_CONTEXT.Provider>
	);
}

/** Get the `showDialog()` function from the current dialog context to show a new dialog. */
export function useDialog(): DialogsStore["showDialog"] {
	const list = useContext(DIALOG_CONTEXT);
	if (!list) throw new StateError("useDialog() must be used inside <Dialogs>");
	return list.showDialog;
}

/** Output the list of dialogs from the current dialog context. */
function DialogList(): React.ReactElement | null {
	const dialogs = useContext(DIALOG_CONTEXT);
	if (!dialogs) throw new StateError("<DialogsList> must be used inside <Dialogs>");
	useStore(dialogs);
	return (
		<>
			{dialogs.value.map(dialog => (
				<Dialog key={dialog.key} onClose={() => setTimeout(() => dialogs.delete(dialog), REMOVE_DELAY)}>
					{dialog.children}
				</Dialog>
			))}
		</>
	);
}

function Dialog({ children, onClose }: { children: React.ReactNode; onClose?: Callback }): React.ReactElement {
	return (
		<dialog
			ref={useMount(_showInNextFrame)}
			onClick={({ currentTarget, target }) => {
				if (currentTarget === target) currentTarget.close(); // Clicking on the dialog itself (but not its contents) closes the dialog.
				if (target instanceof Element && target.closest("a:any-link, nav .button:enabled")) currentTarget.close(); // Clicking on links or `button.option` closes the dialog.
			}}
			onClose={onClose}
		>
			<Suspense fallback={LOADING_ELEMENT}>{children}</Suspense>
			<button type="button" className={`button button-quiet pin-top pin-right`} onClick={closeEventDialog}>
				<Icon icon="close" />
			</button>
		</dialog>
	);
}

function _showInNextFrame(el: HTMLDialogElement): Stop {
	const num = requestAnimationFrame(() => el.show());
	return () => cancelAnimationFrame(num);
}

/** Close the `<dialog>` an `Event` was triggered from inside. */
export const closeEventDialog = ({ target }: { target: unknown }): void => void (target instanceof Element && closeParentDialog(target));

/** Close the `<dialog>` that's the parent of the current element. */
export const closeParentDialog = (el: Element) => el.closest("dialog")?.close();
